import React, { useState } from 'react'
import get from 'lodash/get'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { Link } from 'gatsby'
import Image from 'gatsby-image'
import classNames from 'classnames'
import { MdChevronLeft, MdChevronRight } from 'react-icons/md'

import FadeInItem from '../shared/FadeInItem'
import IconImage from '../IconImage'

import {
  cutDescriptionShort,
  minProductPrice,
  productPrices,
  availableProductSizes,
  productColors,
  productHighlight,
} from '../../utils/helpers'

import { breakpoints, colors, spacing, dimensions } from '../../utils/styles'
import { filteringOptions } from './filteringOptions.data'

const DESCRIPTION_LIMIT = 90
const TRANSITION_DURATION = '250ms'

const ProductListingItemLink = styled(Link)`
  background: ${colors.lightest};
  margin-bottom: ${spacing.lg}px;

  padding-top: 0px;
  padding-left: ${spacing.lg}px;
  padding-right: ${spacing.lg}px;
  padding-bottom: ${spacing['3xl']}px;

  text-decoration: none;
  transition: all ${TRANSITION_DURATION};

  @media (max-width: ${breakpoints.phablet}px) {
    margin-top: ${spacing['2xl']}px;
  }

  @media (min-width: ${breakpoints.phablet}px) and (max-width: ${breakpoints.desktop}px) {
    flex-basis: calc(50%);
    justify-content: center;
    margin-top: ${spacing['2xl']}px;
  }

  @media (min-width: ${breakpoints.desktop}px) {
    flex-basis: calc(33%);
    justify-content: center;
    margin-top: ${spacing.lg}px;
  }
`

const Name = styled(`h5`)`
  color: ${colors.darkest};
  margin: 0;
`

const Description = styled(`p`)`
  color: ${colors.text};
  flex-grow: 1;
`

const PriceRow = styled(`div`)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-left: ${spacing.md}px;
  font-size: 20px;
`

const Price = styled(`p`)`
  text-align: right;
  .price {
    color: ${colors.darkest};
  }
  span {
    color: ${colors.textMild};
    margin-left: ${spacing['2xs']}px;
  }
`

const ProductListingItem = (props) => {
  const {
    product: { title, handle, description, variants, tags, images },
    ...otherProps
  } = props

  const firstVariant = variants[0]
  const [currentVariant, setCurrentVariant] = useState(null)

  let fluid = get(images, '[0].localFile.childImageSharp.fluid')
  if (currentVariant) {
    fluid = get(currentVariant, 'image.localFile.childImageSharp.fluid')
  }
  const size = tags.find((el) => el.indexOf('size:') !== -1)
  let sizeOption = null
  if (size) {
    const sizesFilters = filteringOptions.find((el) => el.type === 'size')
    sizeOption = sizesFilters.options.find((el) => el.filter === size)
  }

  const minPrice = minProductPrice(props.product)
  const allProductPrices = productPrices(props.product)
  const availableSizes = availableProductSizes(
    props.product.variants,
    props.product.tags
  )
  const productColorVariants = props.product.variants
    ? productColors(props.product.variants)
    : []

  const productHighlightLabel = productHighlight(props.product)

  const changeVariant = (direction) => {
    let currentIndex = -1
    let colorVariants = productColorVariants.map((el) => el.variant)
    if (currentVariant) {
      currentIndex = colorVariants
        .map((e) => e.shopifyId)
        .indexOf(currentVariant.shopifyId)
    }

    let nextIndex = currentIndex + direction

    if (nextIndex > colorVariants.length - 1) {
      nextIndex = -1
    } else if (nextIndex < -1) {
      nextIndex = colorVariants.length - 1
    }

    if (nextIndex < colorVariants.length) {
      setCurrentVariant(colorVariants[nextIndex])
    } else {
      setCurrentVariant(null)
    }
  }

  let currentHandle = `/products/${handle}`
  if (currentVariant) {
    currentHandle = `/products/${handle}?variant=${currentVariant.shopifyId}`
  }
  return (
    <ProductListingItemLink to={currentHandle} {...otherProps}>
      <FadeInItem>
        <article className="item">
          <div className="flex-parent flex-parent--column previewContainer">
            <div className={classNames('preview')}>
              {productColorVariants.length > 0 && (
                <>
                  <div
                    className="navigate-variants left"
                    onClick={(event) => {
                      event.preventDefault()
                      changeVariant(-1)
                    }}
                  >
                    <MdChevronLeft style={{ width: '35px', height: '35px' }} />
                  </div>
                  <div
                    className="navigate-variants right"
                    onClick={(event) => {
                      event.preventDefault()
                      changeVariant(+1)
                    }}
                  >
                    <MdChevronRight style={{ width: '35px', height: '35px' }} />
                  </div>
                </>
              )}

              {productHighlightLabel && (
                <div
                  className="productHighlight"
                  style={{
                    backgroundColor: productHighlightLabel.color,
                  }}
                >
                  {productHighlightLabel.imageAlternate && (
                    <img
                      src={productHighlightLabel.imageAlternate}
                      style={{
                        marginRight: `${spacing.xs}px`,
                        height: '22px',
                        width: '22px',
                      }}
                    />
                  )}
                  <h6
                    style={{
                      fontSize: '13px',
                    }}
                  >
                    {productHighlightLabel.title}
                  </h6>
                </div>
              )}

              {fluid ? (
                <Image fluid={fluid} />
              ) : (
                <div
                  style={{
                    width: '100%',
                    paddingBottom: '133.333%',
                    backgroundColor: colors.ligherGrey,
                  }}
                />
              )}
            </div>
          </div>

          <div className="productInfo flex-parent flex-parent--row flex-parent--space-between-main">
            <div>
              <h5 style={{ fontWeight: '500' }}>{title}</h5>
              {productColorVariants.length > 0 && (
                <div className="flex-parent flex-parent--row variantOptions">
                  <p
                    style={{
                      fontWeight: '400',
                      fontStyle: 'italic',
                      color: colors.textMild,
                    }}
                  >
                    optiuni disponibile
                  </p>
                  <div className="flex-parent flex-parent--row">
                    {productColorVariants.map((colorVariant) => (
                      <div
                        key={colorVariant.variant.id}
                        className={classNames('variant', colorVariant.title)}
                        style={{
                          backgroundColor: colorVariant.color,
                        }}
                        onClick={(event) => {
                          event.preventDefault()
                          setCurrentVariant(colorVariant.variant)
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
            <PriceRow>
              <Price>
                {allProductPrices.length > 1 ? (
                  <span className="price"> de la {allProductPrices[0]}</span>
                ) : (
                  <span className="price">{minPrice}</span>
                )}

                <span>RON</span>
              </Price>
            </PriceRow>
          </div>
        </article>
      </FadeInItem>
      <style jsx>{`
        .item {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .previewContainer {
          position: relative;
        }
        .preview {
          position: relative;
          overflow: hidden;
          width: 100%;
          z-index: 2;
          transition: all ${TRANSITION_DURATION};
        }
        .navigate-variants {
          display: flex;
          align-items: center;
          position: relative;
          visibility: hidden;

          position: absolute;
          top: 50%;
          height: 100%;
          transform: translate(0, -50%);
          margin-left: ${spacing.xs}px;
          cursor: pointer;
          z-index: 5;
          color: white;
        }

        .productHighlight {
          display: flex;
          align-items: center;
          position: relative;

          position: absolute;
          top: 15%;
          transform: translate(0, -50%);
          z-index: 5;
          color: white;
          padding: ${1.5 * spacing['2xs']}px;
          padding-left: ${spacing.sm}px;
          padding-right: ${spacing.sm}px;
        }
        .navigate-variants.left:hover {
          color: ${colors.brand};
        }
        .navigate-variants.right:hover {
          color: ${colors.brand};
        }
        .navigate-variants.right {
          right: 0;
          margin-left: unset;
          margin-right: ${spacing.xs}px;
        }

        @media (max-width: ${breakpoints.desktop}px) {
          .item .navigate-variants {
            visibility: visible;
          }
        }

        .item:hover .navigate-variants {
          visibility: visible;
        }

        .variantOptions {
          width: 100%;
          margin-top: ${spacing.md}px;
          align-items: center;
        }
        .variantOptions > p {
          position: absolute;
          opacity: 1;
          transition: opacity 0.3s linear;
        }
        .item:hover .variantOptions > p {
          opacity: 0;
        }

        .variantOptions > div {
          position: absolute;
          opacity: 0;
          transition: opacity 0.3s linear;
        }
        .item:hover .variantOptions > div {
          opacity: 1;
        }

        @media (hover: none) {
          .item .variantOptions > p {
            opacity: 0;
          }
          .variantOptions > div {
            opacity: 1;
          }
        }

        .variant {
          border-radius: 50%;
          width: 20px;
          height: 20px;
          margin-right: ${spacing.xs}px;
          box-shadow: 0 0 2px #888;
        }
        .productInfo {
          padding-top: ${spacing.md}px;
        }
      `}</style>
    </ProductListingItemLink>
  )
}

ProductListingItem.propTypes = {
  product: PropTypes.object.isRequired,
}

export default ProductListingItem
